import debounce from 'lodash.debounce';
import { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import usePortal from '@ezugi/bootstrap/src/hooks/usePortal';
import { DeviceInfo } from '@ezugi/utils/src/helpers/deviceInfo';

import { SpriteAnimation } from '../SpriteAnimation/SpriteAnimation';
import { elasticSpriteContainer, spriteAnimationContainer, spriteAnimationParent } from './ElasticSprite.css';

declare const window: {
  scaleRatio: number
} & Window;

const SpritePositioningMap: Record<string, SpritePositioningAttributes> = {};
const AssetsRatioMap: Record<string, number> = {};

export const setSpritePositioningCache = (key: string, value: SpritePositioningAttributes) => {
  SpritePositioningMap[key] = value;
};

export const setAssetsRatioCache = (key: string, value: number) => {
  AssetsRatioMap[key] = value;
};

const resetSpritePositioningCache = () => {
  Object.keys(SpritePositioningMap).forEach((key) => delete SpritePositioningMap[key]);
};

export const getSpritePositioningCache = (key: string) => SpritePositioningMap[key];
export const getAssetsRatioCache = (key: string) => AssetsRatioMap[key];

const debouncedResizeHandler = debounce(() => {
  resetSpritePositioningCache();
}, 750);

window.addEventListener('resize', debouncedResizeHandler);

type SpritePositioningAttributes = { height: number; top: number; left: number; deg: number }

export type ElementOrigin = {
  top: number;
  left: number;
}

type ElasticSpriteOptions = {
  spriteOffset?: number;
  delay?: number;
  spriteContainerClassName?: string;
  duration?: number;
  loop?: boolean;
  fromOrigin?: ElementOrigin;
  toOrigin?: ElementOrigin;
}

type ElasticSpriteProps = {
  from: string;
  to: string;
  url: string;
  rows: number;
  columns: number;
  options?: ElasticSpriteOptions;
}

export const portalId = 'overlay-animations-root';

const ElasticSprite = memo(({ from, to, url,
  rows, columns, options }:
  ElasticSpriteProps) => {
  const [ positioningData, setPositioningData ] = useState<SpritePositioningAttributes | undefined>();
  const [ spriteAssetRatio, setSpriteAssetRatio ] = useState<number | undefined>();

  const animContainer = usePortal(portalId);

  const animationDuration = options?.duration ?? Number(((rows * columns) / 60).toFixed(2));
  const spriteOffset = options?.spriteOffset ?? 0;
  const animationDelay = options?.delay ?? 0;
  const loop = options?.loop ?? false;
  const spriteContainerClassName = options?.spriteContainerClassName ?? '';
  // Initial bottom center
  const fromOrigin = options?.fromOrigin ?? { top: 100, left: 50 };
  // Initial top center
  const toOrigin = options?.toOrigin ?? { top: 0, left: 50 };

  useEffect(() => {
    const assetRatio = getAssetsRatioCache(url);
    if (assetRatio) {
      setSpriteAssetRatio(assetRatio);
    } else {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        const assetRatioCalc = Number(((img.width / columns) / (img.height / rows)).toFixed(4));
        setAssetsRatioCache(url, assetRatioCalc);
        setSpriteAssetRatio(assetRatioCalc);
      };
    }

    const timeoutId = setTimeout(() => {
      const cachedPositioning = getSpritePositioningCache(`${from}${to}`);

      if (cachedPositioning) {
        setPositioningData(cachedPositioning);
      } else {
        const spritePositioning = computeSpritePositioning(from, to, fromOrigin, toOrigin);
        if (spritePositioning) {
          setSpritePositioningCache(`${from}${to}`, spritePositioning);
          setPositioningData(spritePositioning);
        } else {
          setPositioningData(undefined);
        }
      }
    }, animationDelay);
    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!positioningData || !spriteAssetRatio || !animContainer) return null;

  return createPortal(
    <div
      className={elasticSpriteContainer}
      style={{
        height: positioningData.height,
        top: positioningData.top,
        left: positioningData.left,
        transform: `rotate(${positioningData.deg}deg) translateX(-50%)`,
      }}
    >
      <div className={spriteAnimationParent}>
        <SpriteAnimation
          url={url}
          containerClassName={`${spriteAnimationContainer} ${spriteContainerClassName}`}
          spriteSize={{
            width: (positioningData.height + spriteOffset) * spriteAssetRatio,
            height: positioningData.height + spriteOffset,
          }}
          columns={columns}
          rows={rows}
          animationDuration={animationDuration}
          loop={loop}
        />

      </div>
    </div>,
    animContainer,
  );
});

const HD_RATIO = 16 / 9;
const INVERSE_HD_RATIO = 9 / 16;

const computeSpritePositioning = (
  from: string,
  to: string,
  fromOrigin:ElementOrigin,
  toOrigin:ElementOrigin
): SpritePositioningAttributes | null => {
  const startElement = document.getElementById(from);
  const targetElement = document.getElementById(to);
  const startElementDimmensions = startElement?.getBoundingClientRect();
  const targetElementDimmensions = targetElement?.getBoundingClientRect();

  if (!startElementDimmensions || !targetElementDimmensions) return null;

  const scaleRatio = DeviceInfo.isHandheld ? 1 : window.scaleRatio;

  const startElementYPoint = startElementDimmensions.top + (startElementDimmensions.height * (fromOrigin.top / 100));
  const targetElementYPoint = targetElementDimmensions.top + (targetElementDimmensions.height * (toOrigin.top / 100));
  const startElementXPoint = startElementDimmensions.left + (startElementDimmensions.width * (fromOrigin.left / 100));
  const targetElementXPoint = targetElementDimmensions.left + (targetElementDimmensions.width * (toOrigin.left / 100));

  const distance = Math.sqrt((startElementYPoint - targetElementYPoint) ** 2
    + (startElementXPoint - targetElementXPoint) ** 2) / scaleRatio;

  const deg = Math.atan2(
    startElementXPoint - targetElementXPoint,
    startElementYPoint - targetElementYPoint,
  );

  let resizeTopMargin = 0;
  let resizeLeftMargin = 0;
  if (!DeviceInfo.isHandheld) {
    if (window.innerWidth / window.innerHeight <= HD_RATIO) {
      resizeTopMargin = (window.innerHeight - (window.innerWidth * INVERSE_HD_RATIO)) / 2;
    } else {
      resizeLeftMargin = (window.innerWidth - (window.innerHeight * HD_RATIO)) / 2;
    }
  }

  const top = (startElementYPoint - resizeTopMargin) / scaleRatio;
  const left = (startElementXPoint - resizeLeftMargin) / scaleRatio;

  const positioning = { height: distance, deg: 180 - (deg * (180 / Math.PI)), top, left };
  return positioning;
};

export { ElasticSprite };
